import { useEffect, useMemo, useState } from "react";
import { DateInput } from "@/components/DateInput";
import { toast } from "sonner";
import { api, describeApiError } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowsClockwise, Bank, CheckCircle, CircleNotch, FileText, LockSimple, Plus, Scales, Sparkle, UploadSimple, X } from "@phosphor-icons/react";
import { BalanceSheetCoach } from "@/components/BalanceSheetCoach";
import { RowanTerm } from "@/components/RowanTerm";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const currentMonth = () => new Date().toISOString().slice(0, 7);
const startOfYear = () => `${new Date().getFullYear()}-01-01`;
const fmtMonthLabel = (periodKey) => {
  if (!periodKey) return "this month";
  const [y, m] = periodKey.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
};
const daysBetween = (a, b) => {
  const da = new Date(`${a || "1970-01-01"}T00:00:00`);
  const db = new Date(`${b || "1970-01-01"}T00:00:00`);
  return Math.round(Math.abs((da - db) / 86400000));
};

function MetricCard({ label, value, tone = "var(--bk-text)", testId }) {
  return (
    <div className="bk-card p-5 bk-lift" data-testid={testId}>
      <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-text-2)" }}>{label}</p>
      <p className="font-display text-2xl font-bold mt-2" style={{ color: tone }}>{value}</p>
    </div>
  );
}

function ReportPicker({ value, onChange }) {
  const reports = [
    ["balance_sheet", "Balance Sheet"],
    ["trial_balance", "Trial Balance"],
    ["general_ledger", "General Ledger"],
    ["cash_flow", "Cash Flow"],
    ["profit_and_loss", "Profit & Loss"],
  ];
  return (
    <div className="flex flex-wrap gap-2" data-testid="accounting-report-picker">
      {reports.map(([key, label]) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          data-testid={`report-type-${key}`}
          className="px-3 py-2 rounded-full text-sm font-semibold border"
          style={{
            borderColor: value === key ? "var(--bk-accent)" : "var(--bk-border)",
            color: value === key ? "var(--bk-accent)" : "var(--bk-text)",
            background: value === key ? "#F6F3ED" : "#fff",
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function ProfitAndLossView({ data }) {
  return (
    <div className="space-y-5" data-testid="profit-loss-report">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard label="Income" value={money(data?.total_income)} testId="profit-loss-income" tone="var(--bk-success)" />
        <MetricCard label="Expenses" value={money(data?.total_expense)} testId="profit-loss-expense" tone="#C05621" />
        <MetricCard label="Net Profit" value={money(data?.net_profit)} testId="profit-loss-net" tone={(data?.net_profit || 0) >= 0 ? "var(--bk-accent)" : "#B23B1F"} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[["Income accounts", data?.income_by_category || []], ["Expense accounts", data?.expense_by_category || []]].map(([title, rows], idx) => (
          <div key={title} className="bk-card p-5" data-testid={`profit-loss-section-${idx}`}>
            <h4 className="font-display text-lg font-bold mb-4" style={{ color: "var(--bk-text)" }}>{title}</h4>
            <div className="space-y-2">
              {rows.map((row) => (
                <div key={row.category} className="flex items-center justify-between text-sm" data-testid={`profit-loss-row-${row.category}`}>
                  <span style={{ color: "var(--bk-text)" }}>{row.category}</span>
                  <span className="font-semibold" style={{ color: "var(--bk-text)" }}>{money(row.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BalanceSheetView({ data }) {
  const sections = [
    ["Assets", data?.assets || []],
    ["Liabilities", data?.liabilities || []],
    ["Equity", data?.equity || []],
  ];
  return (
    <div className="space-y-5" data-testid="balance-sheet-report">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard label="Assets" value={money(data?.totals?.assets)} testId="balance-sheet-assets" />
        <MetricCard label="Liabilities" value={money(data?.totals?.liabilities)} testId="balance-sheet-liabilities" />
        <MetricCard label="Equity" value={money(data?.totals?.equity)} testId="balance-sheet-equity" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {sections.map(([title, rows]) => (
          <div key={title} className="bk-card p-5" data-testid={`balance-sheet-section-${title.toLowerCase()}`}>
            <h4 className="font-display text-lg font-bold mb-4" style={{ color: "var(--bk-text)" }}>{title}</h4>
            <div className="space-y-2">
              {rows.map((row) => (
                <div key={row.account_id} className="flex items-center justify-between text-sm" data-testid={`balance-sheet-row-${row.account_id}`}>
                  <span style={{ color: "var(--bk-text)" }}>{row.name}</span>
                  <span className="font-semibold" style={{ color: "var(--bk-text)" }}>{money(row.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrialBalanceView({ data }) {
  return (
    <div className="bk-card overflow-hidden" data-testid="trial-balance-report">
      <div className="p-5 border-b" style={{ borderColor: "var(--bk-border)" }}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h4 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Trial Balance</h4>
          <span className="text-sm font-semibold" style={{ color: data?.is_balanced ? "var(--bk-success)" : "#B23B1F" }} data-testid="trial-balance-status">
            {data?.is_balanced ? "Balanced" : `Out by ${money(data?.difference)}`}
          </span>
        </div>
      </div>
      <div className="divide-y" style={{ borderColor: "var(--bk-border)" }}>
        {(data?.accounts || []).map((row) => (
          <div key={row.id} className="grid grid-cols-4 gap-3 p-4 text-sm" data-testid={`trial-balance-row-${row.id}`}>
            <span style={{ color: "var(--bk-text)" }}>{row.name}</span>
            <span style={{ color: "var(--bk-text-2)" }}>{row.account_type}</span>
            <span className="text-right font-semibold" style={{ color: "var(--bk-text)" }}>{row.report_debit ? money(row.report_debit) : "—"}</span>
            <span className="text-right font-semibold" style={{ color: "var(--bk-text)" }}>{row.report_credit ? money(row.report_credit) : "—"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function GeneralLedgerView({ data }) {
  return (
    <div className="space-y-4" data-testid="general-ledger-report">
      {(data?.accounts || []).map((account) => (
        <div key={account.account_id} className="bk-card overflow-hidden" data-testid={`general-ledger-account-${account.account_id}`}>
          <div className="p-5 border-b" style={{ borderColor: "var(--bk-border)" }}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h4 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>{account.name}</h4>
                <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>{account.account_type} · opening {money(account.opening_balance)} · closing {money(account.closing_balance)}</p>
              </div>
            </div>
          </div>
          <div className="divide-y" style={{ borderColor: "var(--bk-border)" }}>
            {(account.entries || []).map((entry, idx) => (
              <div key={`${account.account_id}-${idx}`} className="grid grid-cols-5 gap-3 p-4 text-sm" data-testid={`general-ledger-line-${account.account_id}-${idx}`}>
                <span style={{ color: "var(--bk-text-2)" }}>{entry.date}</span>
                <span style={{ color: "var(--bk-text)" }}>{entry.memo || entry.description}</span>
                <span className="text-right font-semibold" style={{ color: "var(--bk-text)" }}>{entry.debit ? money(entry.debit) : "—"}</span>
                <span className="text-right font-semibold" style={{ color: "var(--bk-text)" }}>{entry.credit ? money(entry.credit) : "—"}</span>
                <span className="text-right font-semibold" style={{ color: "var(--bk-text)" }}>{money(entry.running_balance)}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function CashFlowView({ data }) {
  const sections = [
    ["Operating", data?.operating],
    ["Investing", data?.investing],
    ["Financing", data?.financing],
  ];
  return (
    <div className="space-y-5" data-testid="cash-flow-report">
      <MetricCard label="Net cash change" value={money(data?.net_change)} testId="cash-flow-net-change" tone={(data?.net_change || 0) >= 0 ? "var(--bk-success)" : "#B23B1F"} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {sections.map(([title, section]) => (
          <div key={title} className="bk-card p-5" data-testid={`cash-flow-section-${title.toLowerCase()}`}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>{title}</h4>
              <span className="font-semibold" style={{ color: "var(--bk-text)" }}>{money(section?.net)}</span>
            </div>
            <div className="space-y-2">
              {(section?.items || []).map((item) => (
                <div key={item.label} className="flex items-center justify-between text-sm" data-testid={`cash-flow-row-${title.toLowerCase()}-${item.label}`}>
                  <span style={{ color: "var(--bk-text)" }}>{item.label}</span>
                  <span className="font-semibold" style={{ color: "var(--bk-text)" }}>{money(item.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Accounting({ businessId, businessName, permissions = {}, initialView = "overview", initialReport = "balance_sheet" }) {
  const [view, setView] = useState(initialView);
  const [overview, setOverview] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [postingAccounts, setPostingAccounts] = useState([]);
  const [journals, setJournals] = useState([]);
  const [batches, setBatches] = useState([]);
  const [activeBatch, setActiveBatch] = useState("");
  const [reconLines, setReconLines] = useState([]);
  const [bookEntries, setBookEntries] = useState([]);
  const [closedPeriods, setClosedPeriods] = useState([]);
  const [reconMonth, setReconMonth] = useState(currentMonth());
  const [selectedLineId, setSelectedLineId] = useState("");
  const [reportType, setReportType] = useState(initialReport);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);
  const [savingJournal, setSavingJournal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [completingBatch, setCompletingBatch] = useState(false);
  const [closingPeriod, setClosingPeriod] = useState(false);
  const [reopeningPeriod, setReopeningPeriod] = useState("");
  const [closeRowan, setCloseRowan] = useState("");
  const [accountForm, setAccountForm] = useState({ name: "", account_type: "Expense", detail_type: "", code: "" });
  const [journalForm, setJournalForm] = useState({
    date: today(),
    memo: "",
    reference: "",
    lines: [{ account_id: "", description: "", debit: "", credit: "" }, { account_id: "", description: "", debit: "", credit: "" }],
  });
  const [reconForm, setReconForm] = useState({
    account_id: "",
    statement_start_balance: "0.00",
    statement_end_balance: "",
    statement_start_date: startOfYear(),
    statement_end_date: today(),
    file: null,
  });
  const [filters, setFilters] = useState({ as_of: today(), start_date: startOfYear(), end_date: today() });
  const canManageAccounts = permissions?.can_manage_accounts !== false;
  const canPostJournal = permissions?.can_post_journal !== false;
  const canDeleteJournal = permissions?.can_delete_journal !== false;
  const canReconcile = permissions?.can_reconcile !== false;
  const canClosePeriods = permissions?.can_close_periods !== false;
  const canReopenPeriods = permissions?.can_reopen_periods !== false;

  const refreshBase = async () => {
    setLoading(true);
    try {
      const [overviewRes, accountRes, journalRes, batchRes, closedRes] = await Promise.all([
        api.get(`/businesses/${businessId}/accounting/overview`),
        api.get(`/businesses/${businessId}/accounts`),
        api.get(`/businesses/${businessId}/journal-entries`),
        api.get(`/businesses/${businessId}/reconciliation/batches`),
        api.get(`/businesses/${businessId}/closed-periods`),
      ]);
      setOverview(overviewRes.data);
      setAccounts(accountRes.data.accounts || []);
      setPostingAccounts(accountRes.data.posting_accounts || []);
      setJournals(journalRes.data.entries || []);
      const nextBatches = batchRes.data.batches || [];
      setBatches(nextBatches);
      setClosedPeriods(closedRes.data.periods || []);
      setReconForm((f) => ({ ...f, account_id: f.account_id || accountRes.data.posting_accounts?.[0]?.id || "" }));
      if (!activeBatch && nextBatches[0]) {
        setActiveBatch(nextBatches[0].id);
        setReconMonth(nextBatches[0].period_key || currentMonth());
      }
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const loadReport = async (nextType = reportType) => {
    setReportLoading(true);
    try {
      const endpoint = {
        balance_sheet: `/businesses/${businessId}/reports/balance-sheet?as_of=${filters.as_of}`,
        trial_balance: `/businesses/${businessId}/reports/trial-balance?as_of=${filters.as_of}`,
        general_ledger: `/businesses/${businessId}/reports/general-ledger?start_date=${filters.start_date}&end_date=${filters.end_date}`,
        cash_flow: `/businesses/${businessId}/reports/cash-flow?start_date=${filters.start_date}&end_date=${filters.end_date}`,
        profit_and_loss: `/businesses/${businessId}/reports/pnl?months=12`,
      }[nextType];
      const { data } = await api.get(endpoint);
      setReportData(data);
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setReportLoading(false);
    }
  };

  const loadSession = async ({ batchId = "", month = reconMonth, accountId = reconForm.account_id } = {}) => {
    if (!month) return;
    setSessionLoading(true);
    try {
      const params = new URLSearchParams({ month });
      if (accountId) params.set("account_id", accountId);
      if (batchId) params.set("batch_id", batchId);
      const { data } = await api.get(`/businesses/${businessId}/reconciliation/session?${params.toString()}`);
      setReconLines(data.lines || []);
      setBookEntries(data.book_entries || []);
      setSelectedLineId((data.lines || [])[0]?.id || "");
      setCloseRowan("");
      if (data.batch?.id) setActiveBatch(data.batch.id);
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setSessionLoading(false);
    }
  };

  useEffect(() => {
    refreshBase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  useEffect(() => {
    if (view === "reports") loadReport(reportType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, reportType]);

  useEffect(() => {
    if (view === "reports") loadReport(reportType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (activeBatch) {
      const batch = batches.find((item) => item.id === activeBatch);
      if (batch) loadSession({ batchId: batch.id, month: batch.period_key || reconMonth, accountId: batch.account_id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBatch, batches]);

  const activeBatchData = useMemo(() => batches.find((batch) => batch.id === activeBatch), [batches, activeBatch]);
  const selectedLine = useMemo(() => reconLines.find((line) => line.id === selectedLineId) || reconLines[0] || null, [reconLines, selectedLineId]);
  const filteredBookEntries = useMemo(() => {
    if (!selectedLine) return bookEntries.slice(0, 12);
    const candidates = selectedLine.candidate_matches?.length
      ? selectedLine.candidate_matches
      : bookEntries.filter((txn) => txn.type === selectedLine.statement_type && Math.abs(Number(txn.amount || 0) - Number(selectedLine.amount || 0)) < 0.01 && daysBetween(txn.date, selectedLine.date) <= 5);
    return candidates.slice(0, 12);
  }, [selectedLine, bookEntries]);

  const addAccount = async () => {
    if (!accountForm.name.trim()) return toast.error("Add an account name first.");
    setSavingAccount(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/accounts`, accountForm);
      setAccounts(data.accounts || []);
      setPostingAccounts(data.posting_accounts || []);
      setAccountForm({ name: "", account_type: "Expense", detail_type: "", code: "" });
      toast.success("Account added to the chart.");
      refreshBase();
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setSavingAccount(false);
    }
  };

  const setJournalLine = (index, key, value) => {
    setJournalForm((form) => ({
      ...form,
      lines: form.lines.map((line, i) => (i === index ? { ...line, [key]: value } : line)),
    }));
  };

  const addJournalLine = () => setJournalForm((form) => ({ ...form, lines: [...form.lines, { account_id: accounts[0]?.id || "", description: "", debit: "", credit: "" }] }));
  const removeJournalLine = (index) => setJournalForm((form) => ({ ...form, lines: form.lines.filter((_, i) => i !== index) }));

  const saveJournal = async () => {
    setSavingJournal(true);
    try {
      const payload = {
        ...journalForm,
        lines: journalForm.lines.map((line) => ({ ...line, debit: Number(line.debit || 0), credit: Number(line.credit || 0) })),
      };
      const { data } = await api.post(`/businesses/${businessId}/journal-entries`, payload);
      setJournals((items) => [data, ...items]);
      setJournalForm({
        date: today(),
        memo: "",
        reference: "",
        lines: [{ account_id: accounts[0]?.id || "", description: "", debit: "", credit: "" }, { account_id: accounts[0]?.id || "", description: "", debit: "", credit: "" }],
      });
      toast.success("Balanced journal entry posted.");
      refreshBase();
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setSavingJournal(false);
    }
  };

  const deleteJournal = async (entryId) => {
    try {
      await api.delete(`/journal-entries/${entryId}`);
      setJournals((items) => items.filter((item) => item.id !== entryId));
      toast.success("Journal entry deleted.");
      refreshBase();
    } catch (err) {
      toast.error(describeApiError(err));
    }
  };

  const uploadStatement = async () => {
    if (!reconForm.file || !reconForm.account_id || !reconForm.statement_end_balance) return toast.error("Add the account, file, and ending balance first.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("account_id", reconForm.account_id);
      fd.append("statement_start_balance", reconForm.statement_start_balance);
      fd.append("statement_end_balance", reconForm.statement_end_balance);
      fd.append("statement_start_date", reconForm.statement_start_date);
      fd.append("statement_end_date", reconForm.statement_end_date);
      fd.append("file", reconForm.file);
      const { data } = await api.post(`/businesses/${businessId}/reconciliation/import-statement`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.rowan_message || (data.imported ? `Imported ${data.imported} statement lines for review.` : "Statement uploaded."));
      setActiveBatch(data.batch.id);
      setReconMonth(data.batch.period_key || reconMonth);
      setReconForm((form) => ({ ...form, statement_end_balance: "", file: null }));
      await refreshBase();
      await loadSession({ batchId: data.batch.id, month: data.batch.period_key || reconMonth, accountId: reconForm.account_id });
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const actOnLine = async (lineId, action, txnId) => {
    try {
      if (action === "approve") await api.post(`/reconciliation/lines/${lineId}/approve`);
      if (action === "skip") await api.post(`/reconciliation/lines/${lineId}/skip`);
      if (action === "match") await api.post(`/reconciliation/lines/${lineId}/match-existing`, { txn_id: txnId });
      await refreshBase();
      await loadSession({ batchId: activeBatch, month: activeBatchData?.period_key || reconMonth, accountId: activeBatchData?.account_id || reconForm.account_id });
    } catch (err) {
      toast.error(describeApiError(err));
    }
  };

  const completeBatch = async () => {
    if (!activeBatchData) return;
    setCompletingBatch(true);
    try {
      await api.post(`/reconciliation/batches/${activeBatchData.id}/complete`);
      toast.success(`Reconciliation completed for ${activeBatchData.period_label || fmtMonthLabel(activeBatchData.period_key)}`);
      await refreshBase();
      await loadSession({ batchId: activeBatchData.id, month: activeBatchData.period_key || reconMonth, accountId: activeBatchData.account_id });
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setCompletingBatch(false);
    }
  };

  const closeCurrentPeriod = async () => {
    if (!activeBatchData) return;
    setClosingPeriod(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/close-period`, { period_key: activeBatchData.period_key || reconMonth, batch_id: activeBatchData.id });
      setCloseRowan(data.rowan_message || "");
      toast.success(`${data.period?.period_label || fmtMonthLabel(reconMonth)} is now locked.`);
      await refreshBase();
      await loadSession({ batchId: activeBatchData.id, month: activeBatchData.period_key || reconMonth, accountId: activeBatchData.account_id });
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setClosingPeriod(false);
    }
  };

  const reopenPeriod = async (periodKey) => {
    setReopeningPeriod(periodKey);
    try {
      const { data } = await api.post(`/businesses/${businessId}/reopen-period`, { period_key: periodKey });
      setCloseRowan(data.rowan_message || "");
      toast.success(`${data.period_label || fmtMonthLabel(periodKey)} is open again.`);
      await refreshBase();
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setReopeningPeriod("");
    }
  };

  if (loading) {
    return (
      <div className="bk-card p-8 flex items-center gap-3" data-testid="accounting-loading">
        <CircleNotch size={20} className="animate-spin" color="var(--bk-accent)" />
        <div>
          <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>Rowan is balancing your books…</p>
          <p className="text-xs" style={{ color: "var(--bk-text-2)" }}>Pulling accounts, journal entries and statements. This takes a moment the first time.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accounting-workspace">
      <div className="p-6 overflow-hidden rounded-[28px] shadow-[0_12px_36px_rgba(26,39,68,0.16)]" style={{ background: "var(--bk-gradient-primary)" }} data-testid="accounting-hero">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "rgba(255,255,255,0.72)" }}>QuickBooks-grade accounting core</p>
            <h3 className="font-display text-3xl font-bold mt-2 text-white">{businessName} accounting center</h3>
            <p className="text-sm mt-2 max-w-xl" style={{ color: "rgba(255,255,255,0.78)" }} data-testid="accounting-hero-copy">
              Double-entry reports, chart of accounts, manual journals, and an AI review queue for bank and credit-card statements — with human approval before anything posts.
            </p>
          </div>
          <button onClick={refreshBase} data-testid="accounting-refresh-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold" style={{ borderColor: "rgba(255,255,255,0.16)", color: "#fff", background: "rgba(255,255,255,0.08)" }}>
            <ArrowsClockwise size={16} weight="bold" /> Refresh numbers
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <MetricCard label="Assets" value={money(overview?.assets)} testId="accounting-assets-card" />
        <MetricCard label="Liabilities" value={money(overview?.liabilities)} testId="accounting-liabilities-card" />
        <MetricCard label="Equity" value={money(overview?.equity)} testId="accounting-equity-card" />
        <MetricCard label="Pending statement approvals" value={String(overview?.pending_reconciliation || 0)} testId="accounting-pending-card" tone={(overview?.pending_reconciliation || 0) > 0 ? "#B45309" : "var(--bk-success)"} />
      </div>

      <Tabs value={view} onValueChange={setView}>
        <TabsList className="bg-white border flex-wrap h-auto rounded-2xl p-2 gap-2" style={{ borderColor: "var(--bk-border)", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
          <TabsTrigger value="overview" data-testid="accounting-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="coach" data-testid="accounting-tab-coach">Coach</TabsTrigger>
          <TabsTrigger value="reports" data-testid="accounting-tab-reports">Reports</TabsTrigger>
          <TabsTrigger value="journal" data-testid="accounting-tab-journal">Journal</TabsTrigger>
          <TabsTrigger value="reconcile" data-testid="accounting-tab-reconcile">Reconcile</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6 space-y-6">
          {!canManageAccounts && (
            <div className="rounded-2xl border px-4 py-3 text-sm" style={{ background: "#fff", borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }} data-testid="accounting-read-only-note">
              Read-only accounting view: you can review the books here, but only owners and partners can change the chart of accounts or reconciliation matches.
            </div>
          )}
          <div className="grid grid-cols-1 xl:grid-cols-[1.1fr_0.9fr] gap-6">
            <div className="bk-card overflow-hidden" data-testid="chart-of-accounts-card">
              <div className="p-5 border-b" style={{ borderColor: "var(--bk-border)" }}>
                <h4 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Chart of accounts</h4>
                <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>Every balance-sheet and profit-and-loss account your reports pull from.</p>
              </div>
              <div className="divide-y" style={{ borderColor: "var(--bk-border)" }}>
                {accounts.map((account) => (
                  <div key={account.id} className="grid grid-cols-[80px_1fr_120px_120px] gap-3 p-4 text-sm" data-testid={`chart-account-${account.id}`}>
                    <span style={{ color: "var(--bk-text-2)" }}>{account.code}</span>
                    <div>
                      <p className="font-semibold" style={{ color: "var(--bk-text)" }}>{account.name}</p>
                      <p className="text-xs" style={{ color: "var(--bk-text-2)" }}>{account.account_type} · {account.detail_type}</p>
                    </div>
                    <span style={{ color: "var(--bk-text-2)" }}>{account.normal_side}</span>
                    <span className="text-right font-semibold" style={{ color: "var(--bk-text)" }}>{money(account.balance)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <div className="bk-card p-5" data-testid="accounting-health-card">
                <div className="flex items-center gap-2 mb-2">
                  <Scales size={18} weight="duotone" color={overview?.trial_balance_ok ? "#15803D" : "#B23B1F"} />
                  <h4 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Books status</h4>
                </div>
                <p className="text-sm" style={{ color: overview?.trial_balance_ok ? "var(--bk-success)" : "#B23B1F" }} data-testid="accounting-balance-status">
                  {overview?.trial_balance_ok ? "Trial balance is in sync." : `Trial balance is off by ${money(overview?.difference)}.`}
                </p>
                <p className="text-sm mt-3" style={{ color: "var(--bk-text-2)" }} data-testid="accounting-journal-count">Manual journals: {overview?.journal_entries || 0}</p>
              </div>

              <div className="bk-card p-5" data-testid="add-account-card">
                <h4 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Add a custom account</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                  <input value={accountForm.name} onChange={(e) => setAccountForm((f) => ({ ...f, name: e.target.value }))} data-testid="account-form-name" className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} placeholder="Account name" />
                  <select value={accountForm.account_type} onChange={(e) => setAccountForm((f) => ({ ...f, account_type: e.target.value }))} data-testid="account-form-type" className="rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: "var(--bk-border)" }}>
                    {['Asset','Liability','Equity','Income','Expense'].map((type) => <option key={type} value={type}>{`${type}`}</option>)}
                  </select>
                  <input value={accountForm.detail_type} onChange={(e) => setAccountForm((f) => ({ ...f, detail_type: e.target.value }))} data-testid="account-form-detail" className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} placeholder="Detail type" />
                  <input value={accountForm.code} onChange={(e) => setAccountForm((f) => ({ ...f, code: e.target.value }))} data-testid="account-form-code" className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} placeholder="Optional code" />
                </div>
                <button onClick={addAccount} disabled={!canManageAccounts || savingAccount} data-testid="account-form-save" className="bk-btn-primary mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-70">
                  {savingAccount ? <><CircleNotch size={16} className="animate-spin" /> Saving…</> : <><Plus size={16} weight="bold" /> Add account</>}
                </button>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="coach" className="mt-6">
          <BalanceSheetCoach
            businessId={businessId}
            permissions={permissions}
            onOpenReconcile={(account) => {
              setView("reconcile");
              if (account?.account_id) {
                setReconForm((f) => ({ ...f, account_id: account.account_id }));
                const month = account?.latest_statement?.period_key || reconMonth;
                setReconMonth(month);
                setTimeout(() => loadSession({ batchId: account?.latest_statement?.id || "", month, accountId: account.account_id }), 0);
              }
            }}
            onOpenReports={() => {
              setReportType("general_ledger");
              setView("reports");
            }}
            onOpenJournal={() => setView("journal")}
          />
        </TabsContent>

        <TabsContent value="reports" className="mt-6 space-y-6">
          <div className="bk-card p-5" data-testid="reports-controls-card">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <ReportPicker value={reportType} onChange={setReportType} />
              <div className="flex flex-wrap gap-3">
                {(reportType === "balance_sheet" || reportType === "trial_balance") ? (
                  <DateInput value={filters.as_of} onChange={(e) => setFilters((f) => ({ ...f, as_of: e.target.value }))} data-testid="reports-as-of-date" className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} />
                ) : (
                  <>
                    <DateInput value={filters.start_date} onChange={(e) => setFilters((f) => ({ ...f, start_date: e.target.value }))} data-testid="reports-start-date" className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} />
                    <DateInput value={filters.end_date} onChange={(e) => setFilters((f) => ({ ...f, end_date: e.target.value }))} data-testid="reports-end-date" className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} />
                  </>
                )}
              </div>
            </div>
          </div>

          {reportLoading ? (
            <div className="bk-card p-8 flex items-center gap-3" data-testid="reports-loading"><CircleNotch size={18} className="animate-spin" /> Loading report…</div>
          ) : reportType === "balance_sheet" ? (
            <BalanceSheetView data={reportData} />
          ) : reportType === "trial_balance" ? (
            <TrialBalanceView data={reportData} />
          ) : reportType === "general_ledger" ? (
            <GeneralLedgerView data={reportData} />
          ) : reportType === "cash_flow" ? (
            <CashFlowView data={reportData} />
          ) : (
            <ProfitAndLossView data={reportData} />
          )}
        </TabsContent>

        <TabsContent value="journal" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_0.95fr] gap-6">
            <div className="bk-card p-5" data-testid="journal-entry-form-card">
              <h4 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}><RowanTerm term="journal">Post a manual journal entry</RowanTerm></h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                <DateInput value={journalForm.date} onChange={(e) => setJournalForm((f) => ({ ...f, date: e.target.value }))} data-testid="journal-date" className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} />
                <input value={journalForm.memo} onChange={(e) => setJournalForm((f) => ({ ...f, memo: e.target.value }))} data-testid="journal-memo" className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} placeholder="Memo" />
                <input value={journalForm.reference} onChange={(e) => setJournalForm((f) => ({ ...f, reference: e.target.value }))} data-testid="journal-reference" className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} placeholder="Reference" />
              </div>

              <div className="space-y-3 mt-4">
                {journalForm.lines.map((line, index) => (
                  <div key={index} className="grid grid-cols-1 sm:grid-cols-[1.1fr_1fr_120px_120px_40px] gap-3 items-center" data-testid={`journal-line-${index}`}>
                    <select value={line.account_id} onChange={(e) => setJournalLine(index, "account_id", e.target.value)} data-testid={`journal-line-account-${index}`} className="rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: "var(--bk-border)" }}>
                      <option value="">Select account</option>
                      {accounts.map((account) => <option key={account.id} value={account.id}>{`${account.code || ''}${account.code ? ' · ' : ''}${account.name}`}</option>)}
                    </select>
                    <input value={line.description} onChange={(e) => setJournalLine(index, "description", e.target.value)} data-testid={`journal-line-description-${index}`} className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} placeholder="Line note" />
                    <input type="number" value={line.debit} onChange={(e) => setJournalLine(index, "debit", e.target.value)} data-testid={`journal-line-debit-${index}`} className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} placeholder="Debit" />
                    <input type="number" value={line.credit} onChange={(e) => setJournalLine(index, "credit", e.target.value)} data-testid={`journal-line-credit-${index}`} className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} placeholder="Credit" />
                    <button onClick={() => removeJournalLine(index)} disabled={journalForm.lines.length <= 2} data-testid={`journal-line-remove-${index}`} className="p-2 rounded-md border disabled:opacity-40" style={{ borderColor: "var(--bk-border)", color: "#B23B1F" }}><X size={14} weight="bold" /></button>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-3 mt-4">
                <button onClick={addJournalLine} disabled={!canPostJournal} data-testid="journal-add-line" className="px-4 py-2 rounded-md border text-sm font-semibold disabled:opacity-60" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}><Plus size={14} weight="bold" className="inline mr-1" />Add line</button>
                <button onClick={saveJournal} disabled={!canPostJournal || savingJournal} data-testid="journal-save-entry" className="bk-btn-primary px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-70">
                  {savingJournal ? <><CircleNotch size={14} className="animate-spin inline mr-2" />Posting…</> : "Post journal entry"}
                </button>
              </div>
            </div>

            <div className="bk-card overflow-hidden" data-testid="recent-journals-card">
              <div className="p-5 border-b" style={{ borderColor: "var(--bk-border)" }}>
                <h4 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Recent manual journals</h4>
              </div>
              <div className="divide-y" style={{ borderColor: "var(--bk-border)" }}>
                {journals.map((entry) => (
                  <div key={entry.id} className="p-4" data-testid={`journal-entry-${entry.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold" style={{ color: "var(--bk-text)" }}>{entry.memo || "Manual journal"}</p>
                        <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{entry.date} · {money(entry.total_debit)} / {money(entry.total_credit)}</p>
                      </div>
                      <button onClick={() => deleteJournal(entry.id)} disabled={!canDeleteJournal} data-testid={`journal-delete-${entry.id}`} className="text-xs font-semibold disabled:opacity-50" style={{ color: "#B23B1F" }}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="reconcile" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 xl:grid-cols-[0.95fr_1.05fr] gap-6">
            <div className="space-y-6">
              <div className="bk-card p-5" data-testid="reconcile-upload-card">
                <div className="flex items-center gap-2 mb-3">
                  <UploadSimple size={18} weight="duotone" color="var(--bk-accent)" />
                  <h4 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}><RowanTerm term="reconcile">Import a statement for approval</RowanTerm></h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <select value={reconForm.account_id} onChange={(e) => setReconForm((f) => ({ ...f, account_id: e.target.value }))} data-testid="reconcile-account-select" className="rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: "var(--bk-border)" }}>
                    <option value="">Choose account</option>
                    {postingAccounts.map((account) => <option key={account.id} value={account.id}>{account.name}</option>)}
                  </select>
                  <input type="number" value={reconForm.statement_end_balance} onChange={(e) => setReconForm((f) => ({ ...f, statement_end_balance: e.target.value }))} data-testid="reconcile-ending-balance" className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} placeholder="Statement ending balance" />
                  <input type="number" value={reconForm.statement_start_balance} onChange={(e) => setReconForm((f) => ({ ...f, statement_start_balance: e.target.value }))} data-testid="reconcile-starting-balance" className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} placeholder="Statement starting balance" />
                  <DateInput value={reconForm.statement_end_date} onChange={(e) => setReconForm((f) => ({ ...f, statement_end_date: e.target.value }))} data-testid="reconcile-ending-date" className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} />
                  <DateInput value={reconForm.statement_start_date} onChange={(e) => setReconForm((f) => ({ ...f, statement_start_date: e.target.value }))} data-testid="reconcile-starting-date" className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} />
                  <input type="file" accept=".csv,.pdf,image/*,.heic,.heif" onChange={(e) => setReconForm((f) => ({ ...f, file: e.target.files?.[0] || null }))} data-testid="reconcile-file-input" className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} />
                </div>
                <p className="text-xs mt-3" style={{ color: "var(--bk-text-2)" }} data-testid="reconcile-upload-help">CSV files preload line-by-line matches. PDFs and statement images still attach the support so you can compare balances right away.</p>
                <button onClick={uploadStatement} disabled={!canReconcile || uploading} data-testid="reconcile-import-button" className="bk-btn-primary mt-4 px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-70">
                  {uploading ? <><CircleNotch size={14} className="animate-spin inline mr-2" />Uploading…</> : "Import for approval"}
                </button>
              </div>

              <div className="bk-card p-5" data-testid="reconcile-session-loader">
                <div className="flex items-center gap-2 mb-3">
                  <Bank size={18} weight="duotone" color="var(--bk-accent)" />
                  <h4 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Load reconciliation session</h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr_auto] gap-3 items-end">
                  <label className="text-xs" style={{ color: "var(--bk-text-2)" }}>Month
                    <input type="month" value={reconMonth} onChange={(e) => setReconMonth(e.target.value)} data-testid="reconcile-session-month" className="mt-1 w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--bk-border)" }} />
                  </label>
                  <label className="text-xs" style={{ color: "var(--bk-text-2)" }}>Account
                    <select value={reconForm.account_id} onChange={(e) => setReconForm((f) => ({ ...f, account_id: e.target.value }))} data-testid="reconcile-session-account" className="mt-1 w-full rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: "var(--bk-border)" }}>
                      <option value="">Choose account</option>
                      {postingAccounts.map((account) => <option key={account.id} value={account.id}>{account.name}</option>)}
                    </select>
                  </label>
                  <button onClick={() => loadSession({ month: reconMonth, accountId: reconForm.account_id })} disabled={sessionLoading} data-testid="reconcile-load-session-button" className="bk-btn-primary px-4 py-2.5 rounded-md text-sm font-semibold disabled:opacity-70">
                    {sessionLoading ? "Loading…" : "Load month"}
                  </button>
                </div>
              </div>

              <div className="bk-card overflow-hidden" data-testid="reconcile-batches-card">
                <div className="p-5 border-b" style={{ borderColor: "var(--bk-border)" }}>
                  <h4 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Statement batches</h4>
                </div>
                <div className="divide-y" style={{ borderColor: "var(--bk-border)" }}>
                  {batches.map((batch) => (
                    <button key={batch.id} onClick={() => { setActiveBatch(batch.id); setReconMonth(batch.period_key || reconMonth); }} data-testid={`reconcile-batch-${batch.id}`} className="w-full text-left p-4 transition-colors" style={{ background: activeBatch === batch.id ? "#F6F3ED" : "#fff" }}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold" style={{ color: "var(--bk-text)" }}>{batch.account_name}</p>
                          <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{batch.period_label || batch.statement_end_date || batch.created_at?.slice(0, 10)} · {batch.pending_count} pending</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: batch.status === "complete" ? "var(--bk-success-bg)" : "#F6EEDF", color: batch.status === "complete" ? "var(--bk-success)" : "#8A6A3B" }}>{batch.status === "complete" ? "Complete" : "In progress"}</span>
                            {batch.is_closed && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#FDECE8", color: "#B23B1F" }}>Closed</span>}
                          </div>
                        </div>
                        <span className="text-xs font-semibold" style={{ color: Math.abs(batch.difference || 0) < 0.01 ? "var(--bk-success)" : "#B45309" }} data-testid={`reconcile-batch-diff-${batch.id}`}>
                          Diff {money(batch.difference)}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="bk-card p-5" data-testid="closed-periods-card">
                <div className="flex items-center gap-2 mb-3">
                  <LockSimple size={18} weight="duotone" color="#FF6B35" />
                  <h4 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Closed periods</h4>
                </div>
                {closedPeriods.length === 0 ? (
                  <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>Nothing locked yet — close a fully reconciled month when you're ready.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {closedPeriods.map((period) => (
                      <span key={period.id} data-testid={`closed-period-${period.period_key}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold" style={{ background: "var(--bk-page)", color: "var(--bk-accent)" }}>
                        <LockSimple size={12} weight="fill" /> {period.period_label}
                        {canReopenPeriods && <button onClick={() => reopenPeriod(period.period_key)} disabled={reopeningPeriod === period.period_key} data-testid={`reopen-period-${period.period_key}`} className="underline underline-offset-4 disabled:opacity-50">{reopeningPeriod === period.period_key ? "Reopening…" : "Reopen"}</button>}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-6">
              {activeBatchData && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <MetricCard label="Statement ending" value={money(activeBatchData.statement_end_balance)} testId="reconcile-statement-ending" />
                  <MetricCard label="Book ending" value={money(activeBatchData.book_ending_balance)} testId="reconcile-book-ending" />
                  <MetricCard label="Difference" value={money(activeBatchData.difference)} testId="reconcile-difference" tone={Math.abs(activeBatchData.difference || 0) < 0.01 ? "var(--bk-success)" : "#B45309"} />
                  <MetricCard label="Pending items" value={String(activeBatchData.pending_count || 0)} testId="reconcile-pending-count" tone={(activeBatchData.pending_count || 0) === 0 ? "var(--bk-success)" : "#B45309"} />
                </div>
              )}

              {activeBatchData && (
                <div className="bk-card p-5" data-testid="reconcile-actions-card">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <h4 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>{activeBatchData.period_label || fmtMonthLabel(activeBatchData.period_key)}</h4>
                      <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>Match, explain, complete, then close the books for that month.</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={completeBatch} disabled={!canReconcile || completingBatch || activeBatchData.pending_count > 0 || activeBatchData.status === "complete" || activeBatchData.is_closed} data-testid="reconcile-complete-button" className="bk-btn-primary px-4 py-2.5 rounded-md text-sm font-semibold disabled:opacity-60">
                        {completingBatch ? "Completing…" : "Complete reconciliation"}
                      </button>
                      <button onClick={closeCurrentPeriod} disabled={!canClosePeriods || closingPeriod || activeBatchData.status !== "complete" || activeBatchData.is_closed} data-testid="reconcile-close-period-button" className="px-4 py-2.5 rounded-md text-sm font-semibold border disabled:opacity-60" style={{ borderColor: "#FF6B35", color: "#FF6B35" }}>
                        {closingPeriod ? "Closing…" : "Close period"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {closeRowan && (
                <div className="bk-rowan-card p-5" data-testid="reconcile-rowan-close-card">
                  <div className="flex items-start gap-3">
                    <Sparkle size={18} weight="fill" color="var(--bk-gold)" className="shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-accent)" }}>Rowan insight</p>
                      <p className="text-sm mt-2" style={{ color: "var(--bk-text)" }}>{closeRowan}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="bk-card overflow-hidden" data-testid="reconcile-lines-card">
                <div className="p-5 border-b" style={{ borderColor: "var(--bk-border)" }}>
                  <h4 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Reconciliation workspace</h4>
                  <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>Bank lines on the left. Matching book entries on the right. Finish every item, then lock the month.</p>
                </div>
                {sessionLoading ? (
                  <div className="p-8 text-sm flex items-center gap-3" data-testid="reconcile-session-loading"><CircleNotch size={18} className="animate-spin" /> Loading session…</div>
                ) : (
                  <div className="grid grid-cols-1 xl:grid-cols-[0.95fr_1.05fr]" style={{ minHeight: 420 }}>
                    <div className="border-r" style={{ borderColor: "var(--bk-border)" }}>
                      <div className="px-5 py-3 text-xs font-semibold uppercase tracking-[0.16em]" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }}>Bank transactions</div>
                      <div className="divide-y max-h-[560px] overflow-y-auto" style={{ borderColor: "var(--bk-border)" }}>
                        {reconLines.length === 0 ? (
                          <div className="p-8 text-sm" style={{ color: "var(--bk-text-2)" }}>Load a month with an imported statement to start matching.</div>
                        ) : reconLines.map((line) => (
                          <button key={line.id} onClick={() => setSelectedLineId(line.id)} data-testid={`reconcile-line-${line.id}`} className="w-full text-left p-4 transition-colors" style={{ background: selectedLine?.id === line.id ? "#F6F3ED" : "#fff" }}>
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="font-semibold" style={{ color: "var(--bk-text)" }}>{line.description}</p>
                                <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{line.date} · {line.account_name} · {line.suggested_category}</p>
                              </div>
                              <div className="text-right shrink-0">
                                <p className="font-semibold" style={{ color: line.statement_type === "income" ? "var(--bk-success)" : "#B23B1F" }} data-testid={`reconcile-line-amount-${line.id}`}>{money(line.amount)}</p>
                                <p className="text-[11px] mt-1 font-semibold" style={{ color: line.status === "pending" ? "#B45309" : line.status === "skipped" ? "#B23B1F" : "var(--bk-success)" }}>{line.status}</p>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="px-5 py-3 text-xs font-semibold uppercase tracking-[0.16em]" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }}>Book entries</div>
                      {!selectedLine ? (
                        <div className="p-8 text-sm" style={{ color: "var(--bk-text-2)" }}>Pick a bank transaction on the left to see matching book entries.</div>
                      ) : (
                        <div className="p-5 space-y-4">
                          <div className="rounded-2xl p-4" style={{ background: "#F6F3ED" }} data-testid="reconcile-selected-line-card">
                            <p className="font-semibold" style={{ color: "var(--bk-text)" }}>{selectedLine.description}</p>
                            <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{selectedLine.date} · {money(selectedLine.amount)} · {selectedLine.statement_type}</p>
                            {selectedLine.status === "pending" && (
                              <div className="flex flex-wrap gap-2 mt-3">
                                <button onClick={() => actOnLine(selectedLine.id, "approve")} disabled={!canReconcile} data-testid={`reconcile-approve-button-${selectedLine.id}`} className="bk-btn-primary px-3 py-2 rounded-md text-xs font-semibold disabled:opacity-60">Approve as new</button>
                                <button onClick={() => actOnLine(selectedLine.id, "skip")} disabled={!canReconcile} data-testid={`reconcile-skip-button-${selectedLine.id}`} className="px-3 py-2 rounded-md border text-xs font-semibold disabled:opacity-60" style={{ borderColor: "var(--bk-border)", color: "#B23B1F" }}>Mark unmatched</button>
                              </div>
                            )}
                          </div>

                          <div className="space-y-3" data-testid="reconcile-book-match-list">
                            {filteredBookEntries.length === 0 ? (
                              <div className="rounded-2xl p-4 text-sm" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }}>No matching book entries yet — approve this bank line as a new book entry if it's legitimate.</div>
                            ) : filteredBookEntries.map((entry) => (
                              <div key={entry.id} className="rounded-2xl border p-4 flex flex-wrap items-center justify-between gap-3" style={{ borderColor: "var(--bk-border)" }} data-testid={`reconcile-book-entry-${entry.id}`}>
                                <div>
                                  <p className="font-semibold" style={{ color: "var(--bk-text)" }}>{entry.description}</p>
                                  <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{entry.date} · {entry.account} · {entry.category}</p>
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                  <span className="text-sm font-semibold" style={{ color: entry.type === "income" ? "var(--bk-success)" : "#B23B1F" }}>{money(entry.amount)}</span>
                                  {selectedLine.status === "pending" && (
                                    <button onClick={() => actOnLine(selectedLine.id, "match", entry.id)} disabled={!canReconcile} data-testid={`reconcile-match-button-${selectedLine.id}-${entry.id}`} className="px-3 py-2 rounded-md border text-xs font-semibold disabled:opacity-60" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>Match existing</button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
